/**
 * Per-conversation sync-cursor persistence.
 *
 * The MLS SDK tracks its own per-conversation `SyncCursor` internally for
 * REST catch-up, but the WS resume frame (services/ws-fanout/src/stream.rs
 * `ClientFrame::Resume`) needs an opaque cursor string we control — the
 * one returned by `GET /v1/messages?conv=&cursor=&limit=` as
 * `FetchResponse.next_cursor`. We capture that cursor every time
 * `PingTransport.fetchSince` succeeds and replay it on the next WS
 * connect so a tab refresh / dropped connection re-joins exactly where
 * it left off — no full sync, no duplicate envelopes (server idempotency
 * dedups any survivors anyway).
 *
 * The store is intentionally minimal: get / put / snapshot. Multiple
 * platforms (web IndexedDB, RN-macOS SQLite, in-memory tests) implement
 * the same interface so PingTransport stays storage-agnostic.
 *
 * Bounded by the number of active conversations — a `delete` is provided
 * for revoke / leave flows but not strictly required (stale entries are
 * harmless: the WS resume just gets a cursor for a conv the device is
 * no longer in, server rejects the per-cursor subscribe silently).
 */
export interface CursorStore {
  /** Returns the opaque cursor previously persisted for this conv, or null. */
  get(conversationIdHex: string): Promise<string | null>;
  /** Persists `cursor` for `conversationIdHex`. Overwrites any prior value. */
  put(conversationIdHex: string, cursor: string): Promise<void>;
  /** Removes the cursor for a conv (used on leave / revoke). Idempotent. */
  delete(conversationIdHex: string): Promise<void>;
  /**
   * Returns every persisted (conv, cursor) pair. Called once at WS connect
   * to build the resume frame; should be O(n) over the user's open
   * conversations — not millions of entries.
   */
  snapshot(): Promise<ReadonlyArray<{ conversationIdHex: string; cursor: string }>>;
}

/**
 * In-memory cursor store. Useful for tests and the legacy in-memory
 * persistence mode. Production hosts swap this for an IndexedDB-backed
 * (web) or SQLite-backed (RN) implementation.
 */
export class MemoryCursorStore implements CursorStore {
  private readonly cursors = new Map<string, string>();

  async get(conversationIdHex: string): Promise<string | null> {
    return this.cursors.get(conversationIdHex.toLowerCase()) ?? null;
  }

  async put(conversationIdHex: string, cursor: string): Promise<void> {
    this.cursors.set(conversationIdHex.toLowerCase(), cursor);
  }

  async delete(conversationIdHex: string): Promise<void> {
    this.cursors.delete(conversationIdHex.toLowerCase());
  }

  async snapshot(): Promise<ReadonlyArray<{ conversationIdHex: string; cursor: string }>> {
    return Array.from(this.cursors.entries()).map(([conversationIdHex, cursor]) => ({
      conversationIdHex,
      cursor,
    }));
  }
}
